<template>
  <div class="home-container">
    <!-- Header Section -->
    <div class="home-header">
      <div class="header-content">
        <div class="greeting-section">
          <h1 class="home-title">Tableau de bord</h1>
          <p class="home-greeting">
            Bonjour, <strong>{{ user?.name || "Utilisateur" }}</strong> 👋
          </p>
          <p class="home-subtitle">
            Voici un aperçu de vos activités et statistiques
          </p>
        </div>
        <div class="header-decoration">
          <div class="decoration-circle decoration-circle--1"></div>
          <div class="decoration-circle decoration-circle--2"></div>
          <div class="decoration-circle decoration-circle--3"></div>
        </div>
      </div>
    </div>

    <!-- Statistics Section -->
    <section class="statistics-wrapper">
      <div class="section-header">
        <h2 class="section-title">
          <i class="bi bi-graph-up-arrow"></i>
          Statistiques
        </h2>
        <button
          class="btn-refresh"
          @click="fetchStatistics"
          :disabled="statisticsLoading"
          title="Actualiser"
        >
          <i
            class="bi bi-arrow-clockwise"
            :class="{ spinning: statisticsLoading }"
          ></i>
        </button>
      </div>

      <div v-if="statisticsLoading" class="statistics-loading">
        <div class="loader"></div>
        <p>Chargement des statistiques...</p>
      </div>

      <div v-else-if="hasStatistics" class="statistics-grid">
        <div class="stat-card stat-card--balance">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Total Ventes Aujourd'hui</h3>
              <p class="stat-value">
                {{ formatCurrency(salesStats.priceTotalTTCSale || 0) }}
              </p>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-graph-up"></i>
            </div>
          </div>
          <div class="stat-bottom-section">
            <div class="stat-details-grid">
              <div class="stat-detail-item">
                <span class="detail-label">Payé</span>
                <span class="detail-value detail-value--positive">{{
                  formatCurrency(salesStats.totalPaidSale || 0)
                }}</span>
              </div>
              <div class="stat-detail-item">
                <span class="detail-label">Impayé</span>
                <span class="detail-value detail-value--negative">{{
                  formatCurrency(salesStats.totalUnpaidSale || 0)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card--today">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Aujourd'hui</h3>
              <div class="stat-metrics-inline">
                <div class="metric-inline">
                  <span class="metric-label-small">Total</span>
                  <span class="metric-value-inline">{{
                    formatCurrency(salesStats.priceTotalTTCSale || 0)
                  }}</span>
                </div>
                <div class="metric-separator"></div>
                <div class="metric-inline">
                  <span class="metric-label-small">Payé</span>
                  <span
                    class="metric-value-inline metric-value-inline--green"
                    >{{ formatCurrency(salesStats.totalPaidSale || 0) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-calendar-day"></i>
            </div>
          </div>
          <div class="stat-progress-bar">
            <div
              class="progress-fill"
              :style="{
                width:
                  (salesStats.priceTotalTTCSale || 0) > 0
                    ? ((salesStats.totalPaidSale || 0) /
                        (salesStats.priceTotalTTCSale || 1)) *
                        100 +
                      '%'
                    : '0%',
              }"
            ></div>
          </div>
        </div>

        <div class="stat-card stat-card--month">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Ce mois</h3>
              <div class="stat-metrics-inline">
                <div class="metric-inline">
                  <span class="metric-label-small">Espèce</span>
                  <span class="metric-value-inline">{{
                    formatCurrency(paymentsStats.totalCashSaleMonth || 0)
                  }}</span>
                </div>
                <div class="metric-separator"></div>
                <div class="metric-inline">
                  <span class="metric-label-small">Total</span>
                  <span
                    class="metric-value-inline metric-value-inline--green"
                    >{{
                      formatCurrency(
                        (paymentsStats.totalCashSaleMonth || 0) +
                          (paymentsStats.totalBankTransferSaleMonth || 0) +
                          (paymentsStats.totalCheckSaleMonth || 0)
                      )
                    }}</span
                  >
                </div>
              </div>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-calendar-month"></i>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card--sales">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Paiements Aujourd'hui</h3>
              <div class="stat-bottom-section">
                <div class="stat-details-grid">
                  <div class="stat-detail-item">
                    <span class="detail-label">Espèce</span>
                    <span class="detail-value detail-value--positive">{{
                      formatCurrency(paymentsStats.totalCashSale || 0)
                    }}</span>
                  </div>
                  <div class="stat-detail-item">
                    <span class="detail-label">Chèque</span>
                    <span class="detail-value">{{
                      formatCurrency(paymentsStats.totalCheckSale || 0)
                    }}</span>
                  </div>
                  <div class="stat-detail-item">
                    <span class="detail-label">Virement</span>
                    <span class="detail-value">{{
                      formatCurrency(paymentsStats.totalBankTransferSale || 0)
                    }}</span>
                  </div>
                  <div class="stat-detail-item">
                    <span class="detail-label">Impayé</span>
                    <span class="detail-value detail-value--negative">{{
                      formatCurrency(salesStats.totalUnpaidSale || 0)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card--balance">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Total Ventes Aujourd'hui</h3>
              <p class="stat-value">
                {{ formatCurrency(salesStats.priceTotalTTCSale || 0) }}
              </p>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-graph-up"></i>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="statistics-empty">
        <i class="bi bi-bar-chart"></i>
        <p>Aucune statistique disponible</p>
      </div>
    </section>

    <!-- Quick Actions Section -->
    <section class="actions-wrapper">
      <div class="section-header">
        <h2 class="section-title">
          <i class="bi bi-grid-3x3-gap"></i>
          Actions rapides
        </h2>
      </div>

      <div class="actions-grid">
        <!-- <button
          @click="navigateToBonCharge"
          class="action-card action-card--primary"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-file-earmark-plus"></i>
            </div>
            <div class="action-pulse"></div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Bon de Charge</h3>
            <p class="action-description">Créer un nouveau bon de charge</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button @click="navigateToStocks" class="action-card action-card--info">
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-box-seam"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Stocks</h3>
            <p class="action-description">Gérer les stocks et inventaires</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToCredits"
          class="action-card action-card--success"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-wallet2"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Crédits</h3>
            <p class="action-description">Consulter les crédits clients</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToCaisse"
          class="action-card action-card--warning"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-cash-coin"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Caisse</h3>
            <p class="action-description">Gérer les paiements et la caisse</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToSales"
          class="action-card action-card--purple"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Ventes</h3>
            <p class="action-description">Consulter et gérer les ventes</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToSellerPayments"
          class="action-card action-card--teal"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-receipt"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Mes Paiements</h3>
            <p class="action-description">Paiements liés à mes ventes</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToCatalog"
          class="action-card action-card--indigo"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-grid-3x3-gap"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Catalogue</h3>
            <p class="action-description">Consulter le catalogue produits</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToItemImageUpdate"
          class="action-card action-card--orange"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-camera"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Images Produits</h3>
            <p class="action-description">Mettre à jour les images produits</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button> -->

        <button @click="navigateToItems" class="action-card action-card--pink">
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-box-seam"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Articles</h3>
            <p class="action-description">Gérer les articles du catalogue</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToQuickSale"
          class="action-card action-card--success"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-cart-plus"></i>
            </div>
            <div class="action-pulse"></div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Nouvelle Vente</h3>
            <p class="action-description">
              Créer une vente avec scan code-barres
            </p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToCustomers"
          class="action-card action-card--info"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-people"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Clients</h3>
            <p class="action-description">
              Gérer les clients et le portefeuille
            </p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToSales"
          class="action-card action-card--purple"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Ventes</h3>
            <p class="action-description">Consulter et gérer les ventes</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToStatistics"
          class="action-card action-card--teal"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-graph-up"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Statistiques</h3>
            <p class="action-description">Voir les statistiques et rapports</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToPayments"
          class="action-card action-card--warning"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-credit-card"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Paiements</h3>
            <p class="action-description">Gérer les paiements</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button @click="navigateToItems" class="action-card action-card--pink">
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-box-seam"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Articles</h3>
            <p class="action-description">Gérer les articles du catalogue</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>

        <button
          @click="navigateToCredits"
          class="action-card action-card--success"
        >
          <div class="action-icon-wrapper">
            <div class="action-icon">
              <i class="bi bi-wallet2"></i>
            </div>
          </div>
          <div class="action-content">
            <h3 class="action-title">Crédits</h3>
            <p class="action-description">Consulter les crédits clients</p>
          </div>
          <div class="action-arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Home",
  data() {
    return {
      statisticsLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      statistics: "sale/getCaisseStatistics",
    }),
    hasStatistics() {
      return this.statistics && Object.keys(this.statistics).length > 0;
    },
    salesStats() {
      return this.statistics?.sales || {};
    },
    paymentsStats() {
      return this.statistics?.payments || {};
    },
  },
  methods: {
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === "")
        return "0,00 MAD";
      const number = Number(amount);
      if (Number.isNaN(number)) return "0,00 MAD";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async fetchStatistics() {
      this.statisticsLoading = true;
      try {
        await this.$store.dispatch("sale/fetchCaisseStatistics");
      } catch (error) {
        console.error("Error loading statistics", error);
        // Don't show error toast, just log it
      } finally {
        this.statisticsLoading = false;
      }
    },
    navigateToBonCharge() {
      this.$router.push({ name: "loadorders-list" });
    },
    navigateToStocks() {
      this.$router.push({ name: "stocks" });
    },
    navigateToCredits() {
      this.$router.push({ name: "credits" });
    },
    navigateToCaisse() {
      this.$router.push({ name: "caisse" });
    },
    navigateToQuickSale() {
      this.$router.push({ name: "quick-sale" });
    },
    navigateToCustomers() {
      this.$router.push({ name: "customers-list" });
    },
    navigateToSales() {
      this.$router.push({ name: "sales-list" });
    },
    navigateToStatistics() {
      this.$router.push({ name: "statistics" });
    },
    navigateToPayments() {
      this.$router.push({ name: "payments-list" });
    },
    navigateToItems() {
      this.$router.push({ name: "items-list" });
    },
  },
  mounted() {
    this.fetchStatistics();
  },
};
</script>

<style scoped>
.home-container {
  padding: 2rem 2.5rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: calc(100vh - 120px);
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Header Section */
.home-header {
  margin-bottom: 3rem;
  position: relative;
}

.header-content {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3);
}

.header-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.decoration-circle--1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.decoration-circle--2 {
  width: 150px;
  height: 150px;
  top: 50%;
  right: 10%;
  animation-delay: 2s;
}

.decoration-circle--3 {
  width: 100px;
  height: 100px;
  bottom: -30px;
  right: 20%;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

.greeting-section {
  position: relative;
  z-index: 1;
  text-align: left;
}

.home-title {
  font-size: 2.75rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 0.75rem 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.02em;
}

.home-greeting {
  font-size: 1.35rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.5rem 0;
  font-weight: 500;
}

.home-greeting strong {
  color: #ffffff;
  font-weight: 700;
}

.home-subtitle {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-weight: 400;
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  color: #6366f1;
  font-size: 1.35rem;
}

.btn-refresh {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  background: #ffffff;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.1rem;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #6366f1;
  background: #f0f4ff;
  transform: rotate(180deg);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

/* Statistics Section */
.statistics-wrapper {
  margin-bottom: 3rem;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  transition: width 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: transparent;
}

.stat-card:hover::before {
  width: 100%;
  opacity: 0.05;
}

.stat-card--balance::before {
  background: #10b981;
}

.stat-card--today::before {
  background: #3b82f6;
}

.stat-card--month::before {
  background: #9333ea;
}

.stat-card--sales::before {
  background: #f59e0b;
}

.stat-card--depot::before {
  background: #14b8a6;
}

.stat-card--stock::before {
  background: #8b5cf6;
}

.stat-card--sale-items::before {
  background: #ec4899;
}

.stat-card--seller-items::before {
  background: #06b6d4;
}

.stat-top-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
}

.stat-info {
  flex: 1;
  min-width: 0;
}

.stat-label {
  margin: 0 0 0.75rem 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.stat-value {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.stat-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.stat-card:hover .stat-icon-circle {
  transform: scale(1.1) rotate(-5deg);
}

.stat-card--balance .stat-icon-circle {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #10b981;
}

.stat-card--today .stat-icon-circle {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #3b82f6;
}

.stat-card--month .stat-icon-circle {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  color: #9333ea;
}

.stat-card--sales .stat-icon-circle {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #f59e0b;
}

.stat-card--depot .stat-icon-circle {
  background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
  color: #14b8a6;
}

.stat-card--stock .stat-icon-circle {
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #8b5cf6;
}

.stat-card--sale-items .stat-icon-circle {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  color: #ec4899;
}

.stat-card--seller-items .stat-icon-circle {
  background: linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%);
  color: #06b6d4;
}

.stat-bottom-section {
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.stat-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.stat-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.detail-value--positive {
  color: #10b981;
}

.detail-value--negative {
  color: #ef4444;
}

.stat-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.stat-metrics-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.metric-inline {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-label-small {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metric-value-inline {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.metric-value-inline--green {
  color: #10b981;
}

.metric-separator {
  width: 1px;
  height: 30px;
  background: linear-gradient(
    180deg,
    transparent,
    #e2e8f0 20%,
    #e2e8f0 80%,
    transparent
  );
}

.stat-progress-bar {
  height: 6px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 1rem;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card--today .progress-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
}

.stat-card--month .progress-fill {
  background: linear-gradient(90deg, #9333ea 0%, #a78bfa 100%);
}

.progress-fill::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.statistics-loading,
.statistics-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  background: #ffffff;
  border-radius: 20px;
  border: 2px dashed #e5e7eb;
  color: #6b7280;
}

.statistics-loading .loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;
}

.statistics-empty i {
  font-size: 3rem;
  color: #cbd5e1;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Actions Section */
.actions-wrapper {
  margin-top: 3rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  width: 100%;
}

.action-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  text-align: left;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  -webkit-tap-highlight-color: transparent;
}

.action-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.action-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.15);
  border-color: transparent;
}

.action-card:hover::after {
  opacity: 1;
}

.action-card--primary {
  border-left: 4px solid #6366f1;
}

.action-card--primary:hover {
  box-shadow: 0 12px 40px rgba(99, 102, 241, 0.25);
  background: linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%);
}

.action-card--info {
  border-left: 4px solid #3b82f6;
}

.action-card--info:hover {
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.25);
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
}

.action-card--success {
  border-left: 4px solid #10b981;
}

.action-card--success:hover {
  box-shadow: 0 12px 40px rgba(16, 185, 129, 0.25);
  background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%);
}

.action-card--warning {
  border-left: 4px solid #f59e0b;
}

.action-card--warning:hover {
  box-shadow: 0 12px 40px rgba(245, 158, 11, 0.25);
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
}

.action-card--purple {
  border-left: 4px solid #9333ea;
}

.action-card--purple:hover {
  box-shadow: 0 12px 40px rgba(147, 51, 234, 0.25);
  background: linear-gradient(135deg, #faf5ff 0%, #ffffff 100%);
}

.action-card--teal {
  border-left: 4px solid #14b8a6;
}

.action-card--teal:hover {
  box-shadow: 0 12px 40px rgba(20, 184, 166, 0.25);
  background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 100%);
}

.action-card--indigo {
  border-left: 4px solid #6366f1;
}

.action-card--indigo:hover {
  box-shadow: 0 12px 40px rgba(99, 102, 241, 0.25);
  background: linear-gradient(135deg, #eef2ff 0%, #ffffff 100%);
}

.action-card--orange {
  border-left: 4px solid #f97316;
}

.action-card--orange:hover {
  box-shadow: 0 12px 40px rgba(249, 115, 22, 0.25);
  background: linear-gradient(135deg, #fff7ed 0%, #ffffff 100%);
}

.action-card--pink {
  border-left: 4px solid #ec4899;
}

.action-card--pink:hover {
  box-shadow: 0 12px 40px rgba(236, 72, 153, 0.25);
  background: linear-gradient(135deg, #fdf2f8 0%, #ffffff 100%);
}

.action-icon-wrapper {
  position: relative;
  flex-shrink: 0;
}

.action-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.action-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: currentColor;
  opacity: 0.2;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0;
  }
}

.action-card--primary .action-icon {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
}

.action-card--info .action-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.action-card--success .action-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.action-card--warning .action-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.action-card--purple .action-icon {
  background: linear-gradient(135deg, #9333ea 0%, #7e22ce 100%);
  color: white;
}

.action-card--teal .action-icon {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  color: white;
}

.action-card--indigo .action-icon {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
}

.action-card--orange .action-icon {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
}

.action-card--pink .action-icon {
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  color: white;
}

.action-card:hover .action-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.action-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.action-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.action-description {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.action-arrow {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.action-card:hover .action-arrow {
  background: #6366f1;
  color: white;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

/* Large Desktop (1440px and above) */
@media screen and (min-width: 1440px) {
  .home-container {
    max-width: 1600px;
    padding: 2.5rem 3rem 5rem;
  }

  .actions-grid {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }
}

/* Desktop (1200px - 1439px) */
@media screen and (max-width: 1439px) and (min-width: 1200px) {
  .home-container {
    padding: 2rem 2.5rem 4rem;
  }
}

/* Large Tablet / Small Desktop (1024px - 1199px) */
@media screen and (max-width: 1199px) and (min-width: 1024px) {
  .home-container {
    padding: 2rem 2rem 4rem;
  }

  .statistics-grid,
  .actions-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .header-content {
    padding: 2.5rem 2rem;
  }

  .home-title {
    font-size: 2.5rem;
  }
}

/* Tablet (768px - 1023px) */
@media screen and (max-width: 1023px) and (min-width: 768px) {
  .home-container {
    padding: 1.75rem 1.5rem 3.5rem;
  }

  .header-content {
    padding: 2.25rem 1.75rem;
    border-radius: 20px;
  }

  .home-title {
    font-size: 2.25rem;
  }

  .home-greeting {
    font-size: 1.25rem;
  }

  .home-subtitle {
    font-size: 1rem;
  }

  .section-title {
    font-size: 1.35rem;
  }

  .section-title i {
    font-size: 1.25rem;
  }

  .statistics-grid,
  .actions-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.25rem;
  }

  .stat-card,
  .action-card {
    padding: 1.75rem;
    border-radius: 18px;
  }

  .stat-value {
    font-size: 1.85rem;
  }

  .action-icon {
    width: 60px;
    height: 60px;
    font-size: 1.6rem;
  }

  .action-title {
    font-size: 1.15rem;
  }

  .action-description {
    font-size: 0.875rem;
  }

  .decoration-circle--1 {
    width: 150px;
    height: 150px;
  }

  .decoration-circle--2 {
    width: 120px;
    height: 120px;
  }

  .decoration-circle--3 {
    width: 80px;
    height: 80px;
  }
}

/* Mobile Landscape / Small Tablet (640px - 767px) */
@media screen and (max-width: 767px) and (min-width: 640px) {
  .home-container {
    padding: 1.5rem 1.25rem 3rem;
  }

  .home-header {
    margin-bottom: 2.5rem;
  }

  .header-content {
    padding: 2rem 1.5rem;
    border-radius: 18px;
  }

  .home-title {
    font-size: 2rem;
  }

  .home-greeting {
    font-size: 1.15rem;
  }

  .home-subtitle {
    font-size: 0.95rem;
  }

  .section-header {
    margin-bottom: 1.25rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .section-title i {
    font-size: 1.15rem;
  }

  .statistics-grid,
  .actions-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .stat-card,
  .action-card {
    padding: 1.5rem;
    border-radius: 16px;
  }

  .stat-top-section {
    margin-bottom: 1.25rem;
    gap: 1.25rem;
  }

  .stat-value {
    font-size: 1.75rem;
  }

  .stat-icon-circle {
    width: 52px;
    height: 52px;
    font-size: 1.4rem;
  }

  .action-card {
    gap: 1.25rem;
  }

  .action-icon {
    width: 56px;
    height: 56px;
    font-size: 1.5rem;
  }

  .action-title {
    font-size: 1.125rem;
  }

  .action-description {
    font-size: 0.85rem;
  }

  .action-arrow {
    width: 44px;
    height: 44px;
    font-size: 1.15rem;
  }

  .decoration-circle--1 {
    width: 120px;
    height: 120px;
    top: -40px;
    right: -40px;
  }

  .decoration-circle--2 {
    width: 100px;
    height: 100px;
  }

  .decoration-circle--3 {
    width: 70px;
    height: 70px;
  }
}

/* Mobile Portrait (480px - 639px) */
@media screen and (max-width: 639px) and (min-width: 480px) {
  .home-container {
    padding: 1.25rem 1rem 2.5rem;
  }

  .home-header {
    margin-bottom: 2rem;
  }

  .header-content {
    padding: 1.75rem 1.25rem;
    border-radius: 16px;
  }

  .home-title {
    font-size: 1.875rem;
  }

  .home-greeting {
    font-size: 1.1rem;
  }

  .home-subtitle {
    font-size: 0.9rem;
  }

  .section-header {
    margin-bottom: 1rem;
  }

  .section-title {
    font-size: 1.15rem;
  }

  .section-title i {
    font-size: 1.05rem;
  }

  .actions-wrapper {
    margin-top: 2rem;
  }

  .statistics-grid,
  .actions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .stat-card,
  .action-card {
    padding: 1.25rem;
    border-radius: 14px;
    gap: 1rem;
  }

  .stat-top-section {
    margin-bottom: 1rem;
    gap: 1rem;
  }

  .stat-value {
    font-size: 1.6rem;
  }

  .stat-icon-circle {
    width: 48px;
    height: 48px;
    font-size: 1.3rem;
  }

  .metric-value-inline {
    font-size: 1rem;
  }

  .action-icon {
    width: 52px;
    height: 52px;
    font-size: 1.4rem;
  }

  .action-title {
    font-size: 1.05rem;
  }

  .action-description {
    font-size: 0.8rem;
  }

  .action-arrow {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }

  .decoration-circle--1 {
    width: 100px;
    height: 100px;
    top: -30px;
    right: -30px;
  }

  .decoration-circle--2 {
    width: 80px;
    height: 80px;
  }

  .decoration-circle--3 {
    width: 60px;
    height: 60px;
  }
}

/* Small Mobile (360px - 479px) */
@media screen and (max-width: 479px) and (min-width: 360px) {
  .home-container {
    padding: 1rem 0.875rem 2rem;
  }

  .home-header {
    margin-bottom: 1.75rem;
  }

  .header-content {
    padding: 1.5rem 1.125rem;
    border-radius: 14px;
  }

  .home-title {
    font-size: 1.65rem;
    margin-bottom: 0.5rem;
  }

  .home-greeting {
    font-size: 1rem;
    margin-bottom: 0.375rem;
  }

  .home-subtitle {
    font-size: 0.85rem;
  }

  .section-header {
    margin-bottom: 1rem;
  }

  .section-title {
    font-size: 1.1rem;
    gap: 0.5rem;
  }

  .section-title i {
    font-size: 1rem;
  }

  .actions-wrapper {
    margin-top: 1.75rem;
  }

  .statistics-grid,
  .actions-grid {
    gap: 0.875rem;
  }

  .stat-card,
  .action-card {
    padding: 1.125rem;
    border-radius: 12px;
    gap: 0.875rem;
  }

  .stat-top-section {
    margin-bottom: 0.875rem;
    gap: 0.875rem;
  }

  .stat-label {
    font-size: 0.7rem;
    margin-bottom: 0.5rem;
  }

  .stat-value {
    font-size: 1.45rem;
  }

  .stat-icon-circle {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
    border-radius: 12px;
  }

  .metric-value-inline {
    font-size: 0.95rem;
  }

  .metric-label-small {
    font-size: 0.65rem;
  }

  .action-icon {
    width: 48px;
    height: 48px;
    font-size: 1.3rem;
    border-radius: 12px;
  }

  .action-title {
    font-size: 1rem;
    margin-bottom: 0.375rem;
  }

  .action-description {
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .action-arrow {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    border-radius: 8px;
  }

  .decoration-circle--1 {
    width: 80px;
    height: 80px;
    top: -25px;
    right: -25px;
  }

  .decoration-circle--2 {
    width: 60px;
    height: 60px;
  }

  .decoration-circle--3 {
    width: 50px;
    height: 50px;
  }
}

/* Extra Small Mobile (below 360px) */
@media screen and (max-width: 359px) {
  .home-container {
    padding: 0.875rem 0.75rem 1.75rem;
  }

  .home-header {
    margin-bottom: 1.5rem;
  }

  .header-content {
    padding: 1.25rem 1rem;
    border-radius: 12px;
  }

  .home-title {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .home-greeting {
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .home-subtitle {
    font-size: 0.8rem;
  }

  .section-header {
    margin-bottom: 0.875rem;
  }

  .section-title {
    font-size: 1rem;
    gap: 0.5rem;
  }

  .section-title i {
    font-size: 0.95rem;
  }

  .actions-wrapper {
    margin-top: 1.5rem;
  }

  .statistics-grid,
  .actions-grid {
    gap: 0.75rem;
  }

  .stat-card,
  .action-card {
    padding: 1rem;
    border-radius: 10px;
    gap: 0.75rem;
  }

  .stat-top-section {
    margin-bottom: 0.75rem;
    gap: 0.75rem;
  }

  .stat-label {
    font-size: 0.65rem;
    margin-bottom: 0.5rem;
  }

  .stat-value {
    font-size: 1.3rem;
  }

  .stat-icon-circle {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    border-radius: 10px;
  }

  .metric-value-inline {
    font-size: 0.9rem;
  }

  .metric-label-small {
    font-size: 0.6rem;
  }

  .action-icon {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
    border-radius: 10px;
  }

  .action-title {
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .action-description {
    font-size: 0.7rem;
    line-height: 1.4;
  }

  .action-arrow {
    width: 36px;
    height: 36px;
    font-size: 0.95rem;
    border-radius: 8px;
  }

  .decoration-circle--1,
  .decoration-circle--2,
  .decoration-circle--3 {
    display: none;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .action-card:hover {
    transform: none;
  }

  .action-card:active {
    transform: scale(0.98);
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
  }

  .stat-card:hover {
    transform: none;
  }

  .btn-refresh:hover:not(:disabled) {
    transform: none;
  }

  .btn-refresh:active:not(:disabled) {
    transform: scale(0.95);
  }
}

/* Landscape orientation adjustments for mobile */
@media screen and (max-width: 767px) and (orientation: landscape) {
  .home-container {
    padding: 1rem 1.25rem 2rem;
  }

  .home-header {
    margin-bottom: 1.5rem;
  }

  .header-content {
    padding: 1.5rem 1.25rem;
  }

  .home-title {
    font-size: 1.75rem;
  }

  .actions-wrapper {
    margin-top: 1.5rem;
  }
}
</style>
